import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
  Paper,
  Chip,
  InputAdornment,
  Autocomplete,
  Breadcrumbs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  SportsKabaddi as CoachIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Repeat as RepeatIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useAuth } from '../../context/AuthContext';
import { coachingProgramAPI, userAPI, amenityAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import { CoachingProgram, CoachingProgramEnrollment, CreateCoachingProgramData, EnrollInProgramData } from '../../types/coaching';
import { User } from '../../types';
import { formatTimeString, formatDateForInput, parseDateOnlyString, formatDate as formatDateUtil } from '../../utils/dateUtils';
import { validateCoachingTimeSlotAlignment, formatSlotAlignmentMessage, dateToTimeString } from '../../utils/slotValidation';
import { amenityScheduleAPI } from '../../services/api';
import ProgramEnrollmentsView from './ProgramEnrollmentsView';
import EnrollMemberToProgramDialog from './EnrollMemberToProgramDialog';
import ReservationResultsDialog from './ReservationResultsDialog';
import { UserRole } from '../../types';

const CoachingProgramManagementPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for coaching programs
  const [coachingPrograms, setCoachingPrograms] = useState<CoachingProgram[]>([]);
  const [coaches, setCoaches] = useState<User[]>([]);
  const [amenities, setAmenities] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [conflictingEnrollments, setConflictingEnrollments] = useState<any[]>([]);
  const [savingProgram, setSavingProgram] = useState<boolean>(false);

  // Reservation result dialog state
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [reservationDialogData, setReservationDialogData] = useState<any>(null);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEnrollmentsDialog, setOpenEnrollmentsDialog] = useState(false);
  const [openEnrollDialog, setOpenEnrollDialog] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<CoachingProgram | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState<string>('');
  const [coachFilter, setCoachFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('SCHEDULED'); // Default to scheduled programs
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    coachId: '',
    isGuestCoach: false,
    guestCoachName: '',
    venues: [] as string[],
    level: '',
    capacity: 8,
    startDate: new Date(),
    endDate: new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    recurringPattern: {
      frequency: 'weekly' as 'daily' | 'weekly' | 'monthly',
      interval: 1,
      daysOfWeek: [1], // Monday by default
      dayOfMonth: undefined as number | undefined
    },
    sessionTime: new Date(),
    sessionDurationMinutes: 60,
    totalCost: '',
    dropInCost: '',
    enrollmentDeadline: null as Date | null,
    allowDropIns: true,
    notes: '',
    image: ''
  });

  // Collapsible section states
  const [recurringConfigExpanded, setRecurringConfigExpanded] = useState(false);
  const [pricingConfigExpanded, setPricingConfigExpanded] = useState(false);

  // Slot validation state
  const [slotValidationWarning, setSlotValidationWarning] = useState<string | null>(null);
  const [venueSchedules, setVenueSchedules] = useState<Map<string, any[]>>(new Map());
  const [venueNames, setVenueNames] = useState<Map<string, string>>(new Map());
  const [loadingVenueSchedules, setLoadingVenueSchedules] = useState<boolean>(false);

  // Fetch data on component mount
  useEffect(() => {
    fetchCoachingPrograms();
    fetchCoaches();
    fetchAmenities();
  }, []);

  // Refetch data when page becomes visible (e.g., navigating back from edit page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCoachingPrograms();
      }
    };

    // Also refresh when window regains focus
    const handleFocus = () => {
      fetchCoachingPrograms();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Refresh data when returning to this route (e.g., after editing a program)
  useEffect(() => {
    // Only refresh if we're not in the initial mount (loading state is false)
    if (!loading) {
      fetchCoachingPrograms();
    }
  }, [location.pathname]);

  const fetchCoachingPrograms = async () => {
    try {
      setLoading(true);
      const response = await coachingProgramAPI.getCoachingPrograms();
      setCoachingPrograms(response.data);
    } catch (error: any) {
      console.error('Error fetching coaching programs:', error);
      setError('Failed to fetch coaching programs');
    } finally {
      setLoading(false);
    }
  };

  const fetchCoaches = async () => {
    try {
      const response = await userAPI.getCoaches();
      setCoaches(response.data);
    } catch (error: any) {
      console.error('Error fetching coaches:', error);
      
      // Extract the error message from the backend response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch coaches';
      
      setError(errorMessage);
    }
  };

  const fetchAmenities = async () => {
    try {
      const response = await amenityAPI.getAmenities();
      setAmenities(response.data);
      
      // Build venue names map
      const names = new Map<string, string>();
      response.data.forEach((amenity: any) => {
        names.set(amenity.id, amenity.name);
      });
      setVenueNames(names);
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
      
      // Extract the error message from the backend response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch amenities';
      
      setError(errorMessage);
    }
  };

  // Fetch schedules for selected venues
  const fetchVenueSchedules = async (venueIds: string[]) => {
    if (venueIds.length === 0) {
      setVenueSchedules(new Map());
      return;
    }

    try {
      setLoadingVenueSchedules(true);
      const schedules = new Map<string, any[]>();

      // Fetch schedules for each venue
      await Promise.all(
        venueIds.map(async (venueId) => {
          try {
            const response = await amenityScheduleAPI.getAmenitySchedules(venueId);
            schedules.set(venueId, response.data);
          } catch (error) {
            console.error(`Error fetching schedule for venue ${venueId}:`, error);
            schedules.set(venueId, []);
          }
        })
      );

      setVenueSchedules(schedules);
    } catch (error) {
      console.error('Error fetching venue schedules:', error);
    } finally {
      setLoadingVenueSchedules(false);
    }
  };

  // Validate slot alignment when venues or times change
  const validateSlotAlignment = async () => {
    if (formData.venues.length === 0) {
      setSlotValidationWarning(null);
      return;
    }

    const startTimeStr = dateToTimeString(formData.sessionTime);
    const endTimeStr = (() => {
      const endDate = new Date(formData.sessionTime);
      endDate.setMinutes(endDate.getMinutes() + formData.sessionDurationMinutes);
      return dateToTimeString(endDate);
    })();

    // Use startDate as representative date for validation
    const result = await validateCoachingTimeSlotAlignment(
      formData.venues,
      venueSchedules,
      venueNames,
      formData.startDate,
      startTimeStr,
      endTimeStr
    );

    if (!result.valid) {
      setSlotValidationWarning(formatSlotAlignmentMessage(result));
    } else if (result.warnings.length > 0) {
      setSlotValidationWarning(formatSlotAlignmentMessage(result));
    } else {
      setSlotValidationWarning(null);
    }
  };

  // Effect to fetch venue schedules when venues change
  useEffect(() => {
    if (formData.venues.length > 0) {
      fetchVenueSchedules(formData.venues);
    } else {
      setVenueSchedules(new Map());
      setSlotValidationWarning(null);
    }
  }, [formData.venues]);

  // Effect to validate slot alignment when schedules are loaded or times change
  useEffect(() => {
    if (venueSchedules.size > 0 && formData.venues.length > 0) {
      validateSlotAlignment();
    }
  }, [venueSchedules, formData.sessionTime, formData.sessionDurationMinutes, formData.startDate]);

  const handleCreateProgram = () => {
    setIsEditing(false);
    setError(null);
    setConflictingEnrollments([]);
    
    const startDate = new Date();
    const startDateDayOfWeek = startDate.getDay(); // Get the day of week for today
    
    setFormData({
      name: '',
      type: '',
      description: '',
      coachId: '',
      isGuestCoach: false,
      guestCoachName: '',
      venues: [],
      level: '',
      capacity: 8,
      startDate: startDate,
      endDate: new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000),
      recurringPattern: {
        frequency: 'weekly',
        interval: 1,
        daysOfWeek: [startDateDayOfWeek], // Auto-select today's day of week
        dayOfMonth: undefined
      },
      sessionTime: new Date(),
      sessionDurationMinutes: 60,
      totalCost: '',
      dropInCost: '',
      enrollmentDeadline: null,
      allowDropIns: true,
      notes: '',
      image: ''
    });
    setRecurringConfigExpanded(false);
    setPricingConfigExpanded(false);
    setOpenCreateDialog(true);
  };

  const handleEditProgram = (program: CoachingProgram) => {
    // Navigate to dedicated edit page
    navigate(`/coaching/programs/edit/${program.id}`);
  };

  const handleDeleteProgram = (program: CoachingProgram) => {
    setSelectedProgram(program);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteProgram = async () => {
    if (!selectedProgram) return;

    try {
      await coachingProgramAPI.deleteCoachingProgram(selectedProgram.id);
      await fetchCoachingPrograms();
      setOpenDeleteDialog(false);
      setSelectedProgram(null);
      setConflictingEnrollments([]);
      setSuccessMessage('Coaching program cancelled successfully');
    } catch (error: any) {
      console.error('Error deleting coaching program:', error);
      
      // Check if error contains conflicting enrollments information
      if (error.response?.data?.activeEnrollments) {
        setConflictingEnrollments(error.response.data.activeEnrollments);
      } else {
        setConflictingEnrollments([]);
      }
      
      // Extract the error message from the backend response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to cancel coaching program';
      
      setError(errorMessage);
    }
  };

  const handleViewEnrollments = (program: CoachingProgram) => {
    setSelectedProgram(program);
    setOpenEnrollmentsDialog(true);
  };

  const handleEnrollMember = (program: CoachingProgram) => {
    setSelectedProgram(program);
    setOpenEnrollDialog(true);
  };

  const handleCopyProgram = (program: CoachingProgram) => {
    setIsEditing(false);
    setError(null);
    setConflictingEnrollments([]);
    
    // Parse times from the original program
    const sessionTime = new Date();
    if (program.sessionTime) {
      const [hours, minutes] = program.sessionTime.split(':').map(Number);
      sessionTime.setHours(hours, minutes, 0, 0);
    }
    
    const startDateParsed = parseDateOnlyString(program.startDate);
    const endDateParsed = parseDateOnlyString(program.endDate);
    const enrollmentDeadlineParsed = program.enrollmentDeadline ? parseDateOnlyString(program.enrollmentDeadline) : null;
    
    // Copy all program data to form
    setFormData({
      name: `${program.name} - Copy`,
      type: program.type,
      description: program.description || '',
      coachId: program.coachId || '',
      isGuestCoach: program.isGuestCoach || !program.coachId,
      guestCoachName: program.guestCoachName || '',
      venues: program.venues || [],
      level: program.level,
      capacity: program.capacity,
      startDate: startDateParsed,
      endDate: endDateParsed,
      recurringPattern: {
        frequency: program.recurringPattern?.frequency || 'weekly',
        interval: program.recurringPattern?.interval || 1,
        daysOfWeek: program.recurringPattern?.daysOfWeek || [startDateParsed.getDay()],
        dayOfMonth: program.recurringPattern?.dayOfMonth
      },
      sessionTime,
      sessionDurationMinutes: program.sessionDurationMinutes || 60,
      totalCost: program.totalCost?.toString() || '',
      dropInCost: program.dropInCost?.toString() || '',
      enrollmentDeadline: enrollmentDeadlineParsed,
      allowDropIns: program.allowDropIns ?? true,
      notes: program.notes || '',
      image: program.image || ''
    });
    
    // Expand configuration sections if there's data
    setRecurringConfigExpanded(!!program.recurringPattern);
    setPricingConfigExpanded(!!(program.totalCost || program.dropInCost));
    
    setOpenCreateDialog(true);
  };

  // Helper function to safely handle date changes from DatePicker
  const handleDateChange = (field: 'startDate' | 'endDate' | 'enrollmentDeadline') => (date: Date | null) => {
    console.log(`DatePicker onChange for ${field}:`, date);
    
    if (!date) {
      if (field === 'enrollmentDeadline') {
        setFormData(prev => ({ ...prev, [field]: null }));
      } else {
        setFormData(prev => ({ ...prev, [field]: new Date() }));
      }
      return;
    }
    
    // Create a new date object that represents the same calendar date
    // regardless of timezone by using the local date components
    const safeDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    console.log(`Safe date for ${field}:`, safeDate);
    
    // If changing the start date and we have a weekly recurring pattern, 
    // replace the old start date's day with the new start date's day
    if (field === 'startDate' && formData.recurringPattern.frequency === 'weekly') {
      const oldDayOfWeek = formData.startDate.getDay();
      const newDayOfWeek = safeDate.getDay();
      const currentDays = formData.recurringPattern.daysOfWeek || [];
      
      // Only update if the day of week has changed
      if (oldDayOfWeek !== newDayOfWeek) {
        // Remove the old day and add the new day
        const updatedDays = currentDays
          .filter(day => day !== oldDayOfWeek) // Remove old day
          .concat(newDayOfWeek) // Add new day
          .sort(); // Keep sorted
        
        setFormData(prev => ({ 
          ...prev, 
          [field]: safeDate,
          recurringPattern: {
            ...prev.recurringPattern,
            daysOfWeek: updatedDays
          }
        }));
        return;
      }
    }
    
    setFormData(prev => ({ ...prev, [field]: safeDate }));
  };

  const handleSubmit = async () => {
    setError(null);
    
    // Collect missing required fields
    const missingFields = [];
    
    if (!formData.name.trim()) {
      missingFields.push('Program Name');
    }
    
    if (!formData.type.trim()) {
      missingFields.push('Program Type');
    }
    
    if (!formData.isGuestCoach && !formData.coachId) {
      missingFields.push('Coach');
    }
    
    if (formData.isGuestCoach && !formData.guestCoachName.trim()) {
      missingFields.push('Guest Coach Name');
    }
    
    if (!formData.venues || formData.venues.length === 0) {
      missingFields.push('Venues');
    }
    
    if (!formData.level.trim()) {
      missingFields.push('Level');
    }
    
    if (formData.capacity < 1) {
      missingFields.push('Valid Capacity');
    }
    
    if (!formData.startDate) {
      missingFields.push('Start Date');
    }
    
    if (!formData.endDate) {
      missingFields.push('End Date');
    }
    
    if (!formData.recurringPattern.frequency) {
      missingFields.push('Recurring Pattern Frequency');
    }
    
    if (!formData.recurringPattern.daysOfWeek || formData.recurringPattern.daysOfWeek.length === 0) {
      missingFields.push('Days of Week (at least one day must be selected)');
    }
    
    if (formData.totalCost === '' || formData.totalCost === null || formData.totalCost === undefined) {
      missingFields.push('Total Cost');
    } else if (parseFloat(formData.totalCost) < 0) {
      missingFields.push('Valid Total Cost (cannot be negative)');
    }
    
    if (!formData.sessionTime) {
      missingFields.push('Session Time');
    }
    
    // If there are missing fields, show a specific error message
    if (missingFields.length > 0) {
      setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    // Validate dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);  
    
    const startDate = new Date(formData.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    // Only validate startDate is not in past for new programs
    if (!isEditing && startDate < today) {
      setError('Cannot schedule program to start in the past');
      return;
    }
    
    // For editing, prevent changing startDate if program has started
    if (isEditing && selectedProgram) {
      const originalStartDate = new Date(selectedProgram.startDate);
      originalStartDate.setHours(0, 0, 0, 0);
      
      if (startDate.getTime() !== originalStartDate.getTime() && 
          (selectedProgram.status === 'ACTIVE' || originalStartDate <= today)) {
        setError('Cannot change the start date of a program that has already started');
        return;
      }
    }
    
    if (formData.startDate >= formData.endDate) {
      setError('End date must be after start date');
      return;
    }
    
    // Validate enrollment deadline
    if (formData.enrollmentDeadline) {
      const deadlineDate = new Date(formData.enrollmentDeadline);
      deadlineDate.setHours(0, 0, 0, 0);
      
      if (deadlineDate < today) {
        setError('Enrollment deadline cannot be in the past');
        return;
      }
      
      if (deadlineDate > startDate) {
        setError('Enrollment deadline cannot be after the program start date');
        return;
      }
    }
    
    try {
      const sessionTime = formData.sessionTime.toTimeString().split(' ')[0].slice(0, 5);
      
      // Calculate end time
      const calculateEndTime = (startTime: string, durationMinutes: number): string => {
        const [hours, minutes] = startTime.split(':').map(Number);
        const date = new Date();
        date.setHours(hours, minutes, 0, 0);
        date.setMinutes(date.getMinutes() + durationMinutes);
        return date.toTimeString().split(' ')[0].slice(0, 5);
      };
      
      const endTime = calculateEndTime(sessionTime, formData.sessionDurationMinutes);
      
      // Enhanced recurring pattern with session timing
      const enhancedRecurringPattern = {
        ...formData.recurringPattern,
        startTime: sessionTime,
        endTime: endTime,
        duration: formData.sessionDurationMinutes
      };

      const programData: CreateCoachingProgramData = {
        name: formData.name,
        type: formData.type,
        description: formData.description,
        coachId: formData.isGuestCoach ? null : formData.coachId,
        isGuestCoach: formData.isGuestCoach,
        guestCoachName: formData.isGuestCoach ? formData.guestCoachName : undefined,
        venues: formData.venues,
        level: formData.level,
        capacity: formData.capacity,
        startDate: formatDateForInput(formData.startDate),
        endDate: formatDateForInput(formData.endDate),
        recurringPattern: enhancedRecurringPattern,
        sessionTime,
        sessionDurationMinutes: formData.sessionDurationMinutes,
        totalCost: formData.totalCost !== '' && formData.totalCost !== null && formData.totalCost !== undefined 
          ? parseFloat(formData.totalCost) 
          : undefined,
        dropInCost: formData.allowDropIns && formData.dropInCost !== '' && formData.dropInCost !== null && formData.dropInCost !== undefined
          ? parseFloat(formData.dropInCost)
          : 0,
        allowDropIns: formData.allowDropIns,
        enrollmentDeadline: formData.enrollmentDeadline ? formatDateForInput(formData.enrollmentDeadline) : undefined,
        notes: formData.notes,
        image: formData.image
      };

      setSavingProgram(true);
      const operationType = isEditing ? 'update' : 'create';
      const response = isEditing && selectedProgram
        ? await coachingProgramAPI.updateCoachingProgram(selectedProgram.id, programData)
        : await coachingProgramAPI.createCoachingProgram(programData);

      // Close dialog and refresh list
      setOpenCreateDialog(false);
      setConflictingEnrollments([]);
      await fetchCoachingPrograms();

      // Show reservation results dialog
      if (response.data.reservationResult) {
        setReservationDialogData({
          entityType: 'program',
          entityName: formData.name,
          operationType,
          reservationResult: response.data.reservationResult
        });
        setReservationDialogOpen(true);
      } else {
        setSuccessMessage(`Coaching program ${operationType}d successfully`);
      }
    } catch (error: any) {
      console.error('Error saving coaching program:', error);
      
      // Check if error contains conflicting enrollments information
      if (error.response?.data?.activeEnrollments) {
        setConflictingEnrollments(error.response.data.activeEnrollments);
      } else {
        setConflictingEnrollments([]);
      }
      
      // Extract the error message from the backend response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          (isEditing ? 'Failed to update coaching program' : 'Failed to create coaching program');
      
      setError(errorMessage);
    } finally {
      setSavingProgram(false);
    }
  };

  const formatTime = (timeString: string) => {
    return formatTimeString(timeString);
  };

  const formatDate = (dateString: string) => {
    return formatDateUtil(dateString);
  };

  const getVenueNames = (venueIds: string[]) => {
    if (!venueIds || venueIds.length === 0) return 'No venues';
    return venueIds
      .map(id => venueNames.get(id) || id)
      .join(', ');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SCHEDULED':
        return 'primary';
      case 'ACTIVE':
        return 'success';
      case 'COMPLETED':
        return 'default';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getDayName = (dayNumber: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayNumber];
  };

  const formatRecurringPattern = (pattern: any) => {
    if (!pattern) return '';
    
    let result = `Every `;
    if (pattern.interval > 1) {
      result += `${pattern.interval} `;
    }
    
    switch (pattern.frequency) {
      case 'daily':
        result += pattern.interval === 1 ? 'day' : 'days';
        break;
      case 'weekly':
        result += pattern.interval === 1 ? 'week' : 'weeks';
        if (pattern.daysOfWeek && pattern.daysOfWeek.length > 0) {
          result += ` on ${pattern.daysOfWeek.map(getDayName).join(', ')}`;
        }
        break;
      case 'monthly':
        result += pattern.interval === 1 ? 'month' : 'months';
        if (pattern.dayOfMonth) {
          result += ` on day ${pattern.dayOfMonth}`;
        }
        break;
    }
    
    return result;
  };

  const handleDayOfWeekChange = (day: number, checked: boolean) => {
    const currentDays = formData.recurringPattern.daysOfWeek || [];
    let newDays;
    
    // Get the day of week for the start date (0 = Sunday, 1 = Monday, etc.)
    const startDateDayOfWeek = formData.startDate.getDay();
    
    if (checked) {
      newDays = [...currentDays, day].sort();
    } else {
      // Prevent unchecking the start date's day of week
      if (day === startDateDayOfWeek) {
        // Show a helpful message to the user
        setError(`Cannot unselect ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day]} as it matches the program start date (${formatDate(formData.startDate.toISOString())}). To change this, update the start date first.`);
        return; // Don't update the state
      }
      newDays = currentDays.filter(d => d !== day);
    }
    
    setFormData({
      ...formData,
      recurringPattern: {
        ...formData.recurringPattern,
        daysOfWeek: newDays
      }
    });
  };

  // Filter programs based on search and filter criteria
  const filteredPrograms = useMemo(() => {
    let filtered = [...coachingPrograms];

    // Text search (program name, description, coach name, guest coach name, type, level)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(program =>
        program.name.toLowerCase().includes(searchLower) ||
        (program.description && program.description.toLowerCase().includes(searchLower)) ||
        (program.coachName && program.coachName.toLowerCase().includes(searchLower)) ||
        (program.guestCoachName && program.guestCoachName.toLowerCase().includes(searchLower)) ||
        (program.type && program.type.toLowerCase().includes(searchLower)) ||
        (program.level && program.level.toLowerCase().includes(searchLower))
      );
    }

    // Coach filter
    if (coachFilter) {
      if (coachFilter === 'guest') {
        // Show guest coaches (those without a coachId or where isGuestCoach is true)
        filtered = filtered.filter(program => !program.coachId || program.isGuestCoach);
      } else {
        // Show specific coach
        filtered = filtered.filter(program => program.coachId === coachFilter);
      }
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(program => program.status === statusFilter);
    }
    // If statusFilter is empty string, show all

    // Level filter
    if (levelFilter) {
      filtered = filtered.filter(program => program.level === levelFilter);
    }

    // Type filter
    if (typeFilter) {
      filtered = filtered.filter(program => program.type === typeFilter);
    }

    return filtered;
  }, [coachingPrograms, searchText, coachFilter, statusFilter, levelFilter, typeFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchText('');
    setCoachFilter('');
    setStatusFilter('SCHEDULED'); // Reset to default
    setLevelFilter('');
    setTypeFilter('');
  };

  // Get unique levels from programs
  const getUniqueLevels = () => {
    const levels = coachingPrograms
      .map(program => program.level)
      .filter(level => level && level.trim() !== '');
    return [...new Set(levels)].sort();
  };

  // Get unique types from programs
  const getUniqueTypes = () => {
    const types = coachingPrograms
      .map(program => program.type)
      .filter(type => type && type.trim() !== '');
    return [...new Set(types)].sort();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <AdminAccessControl allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.EMPLOYEE, UserRole.COACH]}>
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/dashboard"
              color="inherit"
              startIcon={<HomeIcon />}
              sx={{ textTransform: 'none' }}
            >
              Dashboard
            </Button>
            <Typography color="text.primary">Coaching Program Management</Typography>
          </Breadcrumbs>

          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              <RepeatIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Coaching Program Management
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchCoachingPrograms}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateProgram}
                size="large"
              >
                Create Program
              </Button>
            </Stack>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Success Snackbar */}
          <Snackbar
            open={!!successMessage}
            autoHideDuration={4000}
            onClose={() => setSuccessMessage(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setSuccessMessage(null)} severity="success">
              {successMessage}
            </Alert>
          </Snackbar>

          {/* Filters */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  placeholder="Search by program name, description, coach, type, or level..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Coach</InputLabel>
                  <Select
                    value={coachFilter}
                    label="Coach"
                    onChange={(e) => setCoachFilter(e.target.value)}
                  >
                    <MenuItem value="">All Coaches</MenuItem>
                    {coaches.map((coach) => (
                      <MenuItem key={coach.id} value={coach.id}>
                        {coach.firstName} {coach.lastName}
                      </MenuItem>
                    ))}
                    <MenuItem value="guest">Guest Coaches</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="COMPLETED">Completed</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Level</InputLabel>
                  <Select
                    value={levelFilter}
                    label="Level"
                    onChange={(e) => setLevelFilter(e.target.value)}
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    {getUniqueLevels().map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    label="Type"
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {getUniqueTypes().map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  size="small"
                >
                  Clear Filters
                </Button>
              </Box>
            </Stack>
          </Paper>

          {/* Coaching Programs Grid */}
          <Grid container spacing={3}>
            {filteredPrograms.map((program) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={program.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography 
                        variant="h6" 
                        component="h2" 
                        noWrap
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                        }}
                        onClick={() => navigate(`/coaching/programs/${program.id}`)}
                        title="Click to view program"
                      >
                        {program.name}
                      </Typography>
                      <Chip
                        label={program.status}
                        color={getStatusColor(program.status)}
                        size="small"
                      />
                    </Box>
                    
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Coach: {program.coachName}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {getVenueNames(program.venues || [])} • {program.level}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EventIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(program.startDate)} - {formatDate(program.endDate)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RepeatIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {formatRecurringPattern(program.recurringPattern)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {formatTime(program.recurringPattern?.startTime || program.sessionTime)} ({program.recurringPattern?.duration || program.sessionDurationMinutes} min)
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleViewEnrollments(program)}>
                        <GroupIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="primary.main" sx={{ textDecoration: 'underline' }}>
                          {program.currentEnrollments}/{program.capacity} enrolled
                          {program.waitlistCount > 0 && ` • ${program.waitlistCount} waitlisted`}
                        </Typography>
                      </Box>

                      {(program.totalCost || program.dropInCost) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MoneyIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {program.totalCost && `Program: $${program.totalCost}`}
                            {program.totalCost && program.dropInCost && ' • '}
                            {program.dropInCost && `Drop-in: $${program.dropInCost}`}
                          </Typography>
                        </Box>
                      )}

                      {program.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {program.description}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                  
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopyProgram(program)}
                      title="Copy program to create a new one"
                    >
                      Copy
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PersonAddIcon />}
                      onClick={() => handleEnrollMember(program)}
                    >
                      Enroll Member
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {coachingPrograms.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <RepeatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No coaching programs found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create your first coaching program to get started
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateProgram}>
                Create Program
              </Button>
            </Paper>
          ) : filteredPrograms.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <RepeatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No programs match your filters
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Try adjusting your search criteria or clearing filters
              </Typography>
              <Button variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters}>
                Clear Filters
              </Button>
            </Paper>
          )}

        {/* Create/Edit Dialog */}
        <Dialog 
          open={openCreateDialog}
          disableEscapeKeyDown={true}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {isEditing ? 'Edit Coaching Program' : 'Create New Coaching Program'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Program Name */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Program Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>

              {/* Program Type */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <TextField
                    label="Program Type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    fullWidth
                    required
                    placeholder="e.g., Junior Clinic, Adult Training, Advanced Workshop"
                  />
                </FormControl>
              </Grid>

              {/* Coach Selection */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Autocomplete
                    options={coaches}
                    getOptionLabel={(coach) => coach.firstName && coach.lastName ? `${coach.firstName} ${coach.lastName}` : coach.email || 'Unknown Coach'}
                    value={coaches.find(coach => coach.id === formData.coachId) || null}
                    onChange={(event, newValue) => {
                      setFormData({ 
                        ...formData, 
                        coachId: newValue?.id || '',
                        guestCoachName: newValue ? (newValue.firstName && newValue.lastName ? `${newValue.firstName} ${newValue.lastName}` : newValue.email || 'Unknown Coach') : formData.guestCoachName
                      });
                    }}
                    disabled={formData.isGuestCoach}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Coach"
                        required={!formData.isGuestCoach}
                      />
                    )}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isGuestCoach}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          isGuestCoach: e.target.checked,
                          coachId: e.target.checked ? '' : formData.coachId
                        })}
                      />
                    }
                    label="Guest Coach/Instructor"
                  />
                  {formData.isGuestCoach && (
                    <TextField
                      label="Guest Coach Name"
                      value={formData.guestCoachName}
                      onChange={(e) => setFormData({ ...formData, guestCoachName: e.target.value })}
                      fullWidth
                      required
                      placeholder="Enter coach/instructor name"
                    />
                  )}
                </Stack>
              </Grid>

              {/* Venues */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  multiple
                  options={amenities}
                  getOptionLabel={(option) => `${option.name} (${option.type})`}
                  getOptionKey={(option) => option.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={amenities.filter(a => formData.venues.includes(a.id))}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, venues: newValue.map(a => a.id) });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Venues"
                      required
                      placeholder="Select one or more venues/courts"
                      helperText="Select amenities to reserve for this program"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...chipProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={option.id}
                          label={option.name}
                          {...chipProps}
                          size="small"
                        />
                      );
                    })
                  }
                />
              </Grid>

              {/* Slot Alignment Warning */}
              {slotValidationWarning && (
                <Grid size={{ xs: 12 }}>
                  <Alert 
                    severity={slotValidationWarning.includes('❌') ? 'error' : 'warning'}
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    <strong>Venue Slot Alignment:</strong>
                    {loadingVenueSchedules ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Checking venue schedules...</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                        {slotValidationWarning}
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              )}

              {/* Level */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Level"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  fullWidth
                  required
                  placeholder="e.g., Beginner, Intermediate, Advanced"
                />
              </Grid>

              {/* Start Date */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={handleDateChange('startDate')}
                  minDate={new Date()}
                  disabled={!!(isEditing && selectedProgram && (selectedProgram.status === 'ACTIVE' || new Date(selectedProgram.startDate) <= new Date()))}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      required: true,
                      helperText: isEditing && selectedProgram && (selectedProgram.status === 'ACTIVE' || new Date(selectedProgram.startDate) <= new Date()) 
                        ? "Cannot change start date of program that has already started" 
                        : "Cannot schedule program in the past"
                    } 
                  }}
                />
              </Grid>

              {/* End Date */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={handleDateChange('endDate')}
                  minDate={formData.startDate}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      required: true,
                      helperText: "Must be after start date"
                    } 
                  }}
                />
              </Grid>

              {/* Capacity */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                  fullWidth
                  required
                  inputProps={{ min: 1, max: 50 }}
                />
              </Grid>

              {/* Session Time */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TimePicker
                  label="Session Time"
                  value={formData.sessionTime}
                  onChange={(time) => setFormData({ ...formData, sessionTime: time || new Date() })}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* Session Duration */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Session Duration (minutes)"
                  type="number"
                  value={formData.sessionDurationMinutes}
                  onChange={(e) => setFormData({ ...formData, sessionDurationMinutes: parseInt(e.target.value) || 60 })}
                  fullWidth
                  inputProps={{ min: 30, max: 240 }}
                />
              </Grid>

              {/* Enrollment Deadline */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Enrollment Deadline"
                  value={formData.enrollmentDeadline}
                  onChange={handleDateChange('enrollmentDeadline')}
                  minDate={new Date()}
                  maxDate={formData.startDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: "Last day participants can enroll (optional)"
                    }
                  }}
                />
              </Grid>

              {/* Allow Drop-ins */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowDropIns}
                      onChange={(e) => setFormData({ ...formData, allowDropIns: e.target.checked })}
                    />
                  }
                  label="Allow Drop-in Sessions"
                  sx={{ mt: 2 }}
                />
              </Grid>

              {/* Description */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>

            {/* Collapsible Recurring Configuration */}
            <Accordion expanded={recurringConfigExpanded} onChange={() => setRecurringConfigExpanded(!recurringConfigExpanded)} sx={{ mt: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Recurring Schedule Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Frequency */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel>Frequency</InputLabel>
                      <Select
                        value={formData.recurringPattern.frequency}
                        onChange={(e) => setFormData({
                          ...formData,
                          recurringPattern: {
                            ...formData.recurringPattern,
                            frequency: e.target.value as 'daily' | 'weekly' | 'monthly'
                          }
                        })}
                        label="Frequency"
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Interval */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Interval"
                      type="number"
                      value={formData.recurringPattern.interval}
                      onChange={(e) => setFormData({
                        ...formData,
                        recurringPattern: {
                          ...formData.recurringPattern,
                          interval: parseInt(e.target.value) || 1
                        }
                      })}
                      fullWidth
                      inputProps={{ min: 1, max: 12 }}
                      helperText={`Every ${formData.recurringPattern.interval} ${formData.recurringPattern.frequency.slice(0, -2) == "dai" ? "day" : formData.recurringPattern.frequency.slice(0, -2)}${formData.recurringPattern.interval > 1 ? 's' : ''}`}
                    />
                  </Grid>

                  {/* Days of Week (for weekly) */}
                  {formData.recurringPattern.frequency === 'weekly' && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Days of Week *
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        The start date's day ({['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][formData.startDate.getDay()]}) is automatically included and cannot be removed.
                      </Typography>
                      {formData.recurringPattern.daysOfWeek && formData.recurringPattern.daysOfWeek.length === 0 && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                          At least one day of the week must be selected
                        </Alert>
                      )}
                      <FormGroup row>
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => {
                          const isStartDateDay = index === formData.startDate.getDay();
                          return (
                            <FormControlLabel
                              key={index}
                              control={
                                <Checkbox
                                  checked={formData.recurringPattern.daysOfWeek?.includes(index) || false}
                                  onChange={(e) => handleDayOfWeekChange(index, e.target.checked)}
                                  disabled={isStartDateDay} // Disable the start date's day
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {day}
                                  {isStartDateDay && (
                                    <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                                      (Start Date)
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          );
                        })}
                      </FormGroup>
                    </Grid>
                  )}

                  {/* Day of Month (for monthly) */}
                  {formData.recurringPattern.frequency === 'monthly' && (
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Day of Month"
                        type="number"
                        value={formData.recurringPattern.dayOfMonth || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          recurringPattern: {
                            ...formData.recurringPattern,
                            dayOfMonth: parseInt(e.target.value) || undefined
                          }
                        })}
                        fullWidth
                        inputProps={{ min: 1, max: 31 }}
                      />
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Collapsible Pricing Configuration */}
            <Accordion expanded={pricingConfigExpanded} onChange={() => setPricingConfigExpanded(!pricingConfigExpanded)} sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Pricing Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Total Cost */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Total Program Cost"
                      type="number"
                      value={formData.totalCost}
                      onChange={(e) => setFormData({ ...formData, totalCost: e.target.value })}
                      fullWidth
                      required
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Full program enrollment cost"
                    />
                  </Grid>

                  {/* Drop-in Cost */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label="Drop-in Session Cost"
                      type="number"
                      value={formData.dropInCost}
                      onChange={(e) => setFormData({ ...formData, dropInCost: e.target.value })}
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Per-session cost for drop-ins"
                      disabled={!formData.allowDropIns}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Notes */}
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              sx={{ mt: 2 }}
              placeholder="Internal notes about this program"
            />
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 2, alignItems: 'stretch' }}>
            {/* Error Alert inside Dialog */}
            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {error}
                {conflictingEnrollments.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Active enrollments that would be affected:
                    </Typography>
                    {conflictingEnrollments.map((enrollment: any, index: number) => (
                      <Typography key={index} variant="body2" sx={{ fontSize: '0.875rem', ml: 1 }}>
                        • {enrollment.memberName || enrollment.guestName} ({enrollment.memberEmail || enrollment.guestEmail})
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Box>
                {isEditing && (
                  <Button 
                    onClick={() => {
                      setOpenCreateDialog(false);
                      setOpenDeleteDialog(true);
                    }}
                    color="error" 
                    startIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => setOpenCreateDialog(false)} disabled={savingProgram}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={savingProgram}>
                  {savingProgram ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {isEditing ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    isEditing ? 'Update' : 'Create'
                  )}
                </Button>
              </Box>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} disableEscapeKeyDown={true}>
          <DialogTitle>Cancel Coaching Program</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to cancel "{selectedProgram?.name}"? This action will mark the program as cancelled.
            </DialogContentText>
            
            {/* Error Alert in Delete Dialog */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
                {conflictingEnrollments.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Active enrollments that must be handled first:
                    </Typography>
                    {conflictingEnrollments.map((enrollment: any, index: number) => (
                      <Typography key={index} variant="body2" sx={{ fontSize: '0.875rem', ml: 1 }}>
                        • {enrollment.memberName || enrollment.guestName} ({enrollment.memberEmail || enrollment.guestEmail})
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenDeleteDialog(false);
              setError(null);
              setConflictingEnrollments([]);
            }}>Cancel</Button>
            <Button onClick={confirmDeleteProgram} color="error" variant="contained">
              Cancel Program
            </Button>
          </DialogActions>
        </Dialog>

        {/* Program Enrollments Dialog */}
        {openEnrollmentsDialog && selectedProgram && (
          <Dialog 
            open={openEnrollmentsDialog}
            disableEscapeKeyDown={true}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>
              Program Enrollments - {selectedProgram.name}
            </DialogTitle>
            <DialogContent>
              <ProgramEnrollmentsView 
                program={selectedProgram}
                venueNames={venueNames}
                onEnrollmentUpdated={() => {
                  fetchCoachingPrograms(); // Refresh programs to update enrollment counts
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEnrollmentsDialog(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Enroll Member to Program Dialog */}
        <EnrollMemberToProgramDialog
          open={openEnrollDialog}
          onClose={() => setOpenEnrollDialog(false)}
          program={selectedProgram}
          onSuccess={(message) => {
            setSuccessMessage(message || 'Member enrolled successfully!');
            fetchCoachingPrograms(); // Refresh programs to update enrollment counts
          }}
        />

        {/* Reservation Results Dialog */}
        <ReservationResultsDialog
          open={reservationDialogOpen}
          onClose={() => {
            setReservationDialogOpen(false);
            setReservationDialogData(null);
          }}
          entityType={reservationDialogData?.entityType || 'program'}
          entityName={reservationDialogData?.entityName || ''}
          operationType={reservationDialogData?.operationType || 'create'}
          reservationResult={reservationDialogData?.reservationResult || null}
        />
        
      </AdminAccessControl>
      </Container>
      
    </LocalizationProvider>
    
  );
};

export default CoachingProgramManagementPage;
